import { Avatar } from '@lmc/ui-kit'
import type { FormInstance } from 'antd'
import type { InternalNamePath } from 'antd/lib/form/interface'
import { showToast } from 'common/utils'

/**
 * Filters out clocked-in team members from grouped carer options.
 * Similar to how quick allocation works - clocked-in team members
 * should not appear in the dropdown options (but will still appear
 * in selected values).
 *
 * @param groupedCarerOptions - The grouped carer options to filter
 * @param clockIns - Array of clock-in objects from formData
 * @param shouldFilter - Whether to apply the filter (typically based on feature flag)
 * @returns Filtered grouped carer options
 */
export const filterClockedInTeamMembers = (
  groupedCarerOptions: any[],
  clockIns: any[] | undefined,
  shouldFilter: boolean
) => {
  if (!shouldFilter || !clockIns || clockIns.length === 0) {
    return groupedCarerOptions
  }

  const clockedInTeamMemberIds = clockIns
    .map((clockIn: any) => clockIn?.teamMemberId)
    .filter((id: string) => id)

  if (clockedInTeamMemberIds.length === 0) {
    return groupedCarerOptions
  }

  const filtered = groupedCarerOptions
    .map((group) => ({
      ...group,
      options:
        group.options?.filter(
          (option: any) => !clockedInTeamMemberIds.includes(option.value)
        ) || []
    }))
    .filter((group) => group.options && group.options.length > 0)

  return filtered
}

export const handleOnFinishFailed = (
  errorFields: {
    name: InternalNamePath
    errors: string[]
  }[]
) =>
  errorFields.map((errorField) =>
    errorField.errors.map((err) => showToast({ title: err, type: 'error' }))
  )

export const handleSubmit = async (
  form: FormInstance,
  handleOnFinish: (values: any) => Promise<void>
) => {
  try {
    const values = await form.validateFields()
    await handleOnFinish(values)
  } catch (errorInfo) {
    const error = errorInfo as {
      errorFields: { name: string[]; errors: string[] }[]
    }
    handleOnFinishFailed(error.errorFields)
  }
}

export const cleanDecimalValues = (value?: number) =>
  value ? Math.round(value || 0).toString() : ''

export const getMaxValueValidator = async (
  value: string,
  error: string,
  maxValue?: number
) => {
  if (!value) {
    return
  }

  if (maxValue && +value > maxValue) {
    throw new Error(error)
  }
}

/**
 * Converts allocation list API response to grouped options format for MultiSelect.
 * Groups users by active/inactive status and includes avatar images.
 *
 * @param allocationList - Array of users from allocation list API
 * @param fallbackOptions - Fallback grouped options if allocation list is empty
 * @returns Grouped options in MultiSelect format
 */
export const convertAllocationListToGroupedOptions = (
  allocationList: any[],
  fallbackOptions: any[]
) => {
  if (!allocationList || allocationList.length === 0) {
    return fallbackOptions
  }

  // Group by active/inactive status
  const grouped: { active: any[]; inactive: any[] } = {
    active: [],
    inactive: []
  }

  allocationList.forEach((user: any) => {
    // Use avatar or picture property (allocation list might use different property name)
    // Always render Avatar component - it will show default if URL is missing
    const avatarUrl = user.avatar || user.picture || undefined

    const option = {
      label: `${user.name.first} ${user.name.last}`,
      value: user.id,
      image: <Avatar url={avatarUrl as unknown as string} size={20} />
    }

    if (user.active) {
      grouped.active.push(option)
    } else {
      grouped.inactive.push(option)
    }
  })

  // Convert to MultiSelect format
  const result = []
  if (grouped.active.length > 0) {
    result.push({
      label: 'ACTIVE',
      options: grouped.active.sort((a, b) => a.label.localeCompare(b.label))
    })
  }
  if (grouped.inactive.length > 0) {
    result.push({
      label: 'INACTIVE',
      options: grouped.inactive.sort((a, b) => a.label.localeCompare(b.label))
    })
  }

  return result.length > 0 ? result : fallbackOptions
}
